import * as React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';

export interface MultiSelectProps<T extends number | string> {
  // value is an array of ids (numbers for enums, strings for names)
  value?: Array<T> | null;
  onChange: (ids: Array<T> | null, options: Array<{ id: T; name: string; }> | null) => void;
  options?: Array<{ id: T; name: string; code?: string }>;
  label?: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  sx?: any;
}

export const EnumMultiSelect = <T extends number | string>(
  props: MultiSelectProps<T>
) => {
  const {
    value,
    options = [],
    onChange,
    label = "",
    placeholder = "",
    size = "medium",
    fullWidth = true,
    disabled,
    required,
    error,
    helperText,
    sx,
  } = props;

  const selectedOptions = React.useMemo(() => {
    if (!value) return [];
    const set = new Set(value);
    return options.filter(o => set.has(o.id));
  }, [value, options]);

  return (
    <Autocomplete<{ id: T; name: string; }, true, false, false>
      multiple
      options={options}
      value={selectedOptions}
      onChange={(_, opts) => {
        const selected = opts ?? [];
        const ids = selected.map(o => o.id);
        onChange(ids.length ? ids : null, selected.length ? selected : null);
      }}
      getOptionLabel={opt => opt.name}
      isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
      autoHighlight
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
        >
          {option.name}
        </Box>
      )}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder={!value?.length ? placeholder : ''}
          size={size}
          fullWidth={fullWidth}
          disabled={disabled}
          required={required}
          error={error}
          helperText={helperText}
          // force placeholder text to appear instead of label when no value is selected
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
      componentsProps={{
        paper: {
          sx: (t) => ({
            border: '1px solid',
            borderColor: t.palette.primary.dark,
            borderRadius: 1,
          }),
        }
      }}

      sx={{ ...(sx || {}) }}
    />
  );
};

export default EnumMultiSelect;
